import type { Feature, FeatureCollection, Polygon } from 'geojson';
import type { Hole, LngLat } from './types.ts';
import { bearingDeg, destination } from './geo.ts';

/** Metre-accurate circle as a polygon — MapLibre circle layers are pixel based. */
export function circlePolygon(
  centre: LngLat,
  radiusM: number,
  props: Record<string, unknown> = {},
  steps = 48
): Feature<Polygon> {
  const ring: LngLat[] = [];
  for (let i = 0; i <= steps; i++) {
    ring.push(destination(centre, (i * 360) / steps, radiusM));
  }
  return { type: 'Feature', properties: props, geometry: { type: 'Polygon', coordinates: [ring] } };
}

export const fc = (features: Feature[]): FeatureCollection => ({
  type: 'FeatureCollection',
  features,
});

export const emptyFc: FeatureCollection = { type: 'FeatureCollection', features: [] };

/**
 * Buffer a polyline into a polygon — the strip of land a hole is played down.
 *
 * Walks one side of the line, then back along the other, with rounded caps at
 * the tee and the green. Follows the mapped centreline, so a dogleg bends
 * rather than cutting a straight corridor through the trees.
 */
export function corridorPolygon(
  line: LngLat[],
  widthM: number,
  props: Record<string, unknown> = {}
): Feature<Polygon> {
  const pts = line.filter(
    (p, i) => i === 0 || Math.abs(p[0] - line[i - 1][0]) > 1e-9 || Math.abs(p[1] - line[i - 1][1]) > 1e-9
  );
  if (pts.length < 2) {
    return circlePolygon(pts[0] ?? [0, 0], widthM, props);
  }

  const half = widthM / 2;
  const bearingAt = (i: number) => {
    if (i === 0) return bearingDeg(pts[0], pts[1]);
    if (i === pts.length - 1) return bearingDeg(pts[pts.length - 2], pts[pts.length - 1]);
    // Average the two segments so the corridor turns smoothly at a dogleg.
    const a = bearingDeg(pts[i - 1], pts[i]);
    const b = bearingDeg(pts[i], pts[i + 1]);
    const diff = (((b - a + 540) % 360) - 180) / 2;
    return (a + diff + 360) % 360;
  };

  const left: LngLat[] = [];
  const right: LngLat[] = [];
  pts.forEach((p, i) => {
    const br = bearingAt(i);
    left.push(destination(p, (br + 90) % 360, half));
    right.push(destination(p, (br + 270) % 360, half));
  });

  // Rounded caps so the ends read as "the hole starts/finishes here" rather
  // than as an arbitrary chopped-off box.
  //
  // Direction matters and is easy to get wrong: the ring arrives at the cap on
  // the LEFT side (bearing +90) and must leave on the RIGHT (-90), so the arc
  // has to sweep DOWN from +90 to -90. Sweeping the other way folds the ring
  // back on itself and produces a bowtie — a polygon with a dozen
  // self-intersections whose hit-testing is meaningless.
  const cap = (centre: LngLat, fromBr: number): LngLat[] => {
    const arc: LngLat[] = [];
    // Exclusive of +-90: those two points are already the last vertices of the
    // left and right sides, and repeating them leaves zero-length segments in
    // the ring that some geometry consumers treat as degenerate.
    for (let a = 90 - 22.5; a > -90; a -= 22.5) {
      arc.push(destination(centre, (fromBr + a + 360) % 360, half));
    }
    return arc;
  };

  const startBr = bearingAt(0);
  const endBr = bearingAt(pts.length - 1);
  const ring: LngLat[] = [
    ...left,
    ...cap(pts[pts.length - 1], endBr),
    ...right.slice().reverse(),
    ...cap(pts[0], (startBr + 180) % 360),
  ];
  ring.push(ring[0]);

  return { type: 'Feature', properties: props, geometry: { type: 'Polygon', coordinates: [ring] } };
}

/** Width of a hole's playing corridor, in metres. */
export const CORRIDOR_WIDTH_M = 90;

/**
 * The spine of a hole: its mapped centreline where one exists, otherwise a
 * straight tee-to-green line.
 */
export function holeSpine(hole: Hole, teeColour: string): LngLat[] {
  if (hole.centreline && hole.centreline.length >= 2) return hole.centreline;
  const tee = hole.tees[teeColour] ?? hole.tees.White ?? hole.green.middle;
  return [tee, hole.green.middle];
}

/**
 * The corridor ring for a hole.
 *
 * Single source of truth on purpose: the map draws this polygon AND the aim
 * tool tests taps against it. If the two were computed separately they would
 * drift, and a golfer would tap inside the blue and have it rejected — which
 * reads as the app being broken rather than as a rule.
 */
export function holeCorridorRing(hole: Hole, teeColour: string): LngLat[] {
  const poly = corridorPolygon(holeSpine(hole, teeColour), CORRIDOR_WIDTH_M);
  return poly.geometry.coordinates[0] as LngLat[];
}

/**
 * A dimming mask: the whole world, with the playing corridor cut out of it.
 *
 * GeoJSON polygons take an outer ring plus holes, so one feature can darken
 * everything except the hole being played. Two of these at different widths
 * stack into a graded falloff — a single hard-edged mask reads as a rendering
 * bug rather than as focus.
 *
 * 🟥 DIM, never black. A golfer whose ball is two fairways over still has to
 * see where it went, and the walk to the next tee runs outside the corridor.
 * Hiding that would trade one small annoyance for a bigger one.
 */
export function corridorMask(
  line: LngLat[],
  widthM: number,
  props: Record<string, unknown> = {}
): Feature<Polygon> {
  const cut = corridorPolygon(line, widthM);
  const ring = cut.geometry.coordinates[0];
  return {
    type: 'Feature',
    properties: props,
    geometry: {
      type: 'Polygon',
      // Outer ring is the whole sphere; the corridor is the hole in it.
      coordinates: [
        [[-180, -85], [180, -85], [180, 85], [-180, 85], [-180, -85]],
        // Wound the other way so it reads as a hole, not a second island.
        [...ring].reverse(),
      ],
    },
  };
}

/**
 * The inverse of the course: everything that is NOT this golf course.
 *
 * Hybrid mode wants aerial imagery ON the course and flat turf everywhere else.
 * MapLibre cannot clip a raster to a shape, so this does the opposite and it
 * amounts to the same picture: draw the imagery full-bleed, then lay THIS on
 * top of it. A polygon may carry many holes, so the outer ring is the world and
 * every fairway corridor is punched out of it — the imagery shows through the
 * course and is covered everywhere else.
 *
 * Why it is worth doing at all: an aerial of a residential golf estate is
 * mostly rooftops, driveways and parked cars, none of which is information to a
 * golfer, and all of which is visual noise competing with the hole. Cutting the
 * imagery back to the playing surface leaves the fairway as the only detailed
 * thing on screen.
 *
 * Corridors are widened past CORRIDOR_WIDTH_M on purpose: the playing corridor
 * is the strip you play down, but the imagery should reach the rough, the
 * bunkers just off it and the whole green complex, or the course looks like it
 * was cut out with scissors.
 */
export function courseImageryMask(
  holes: { number: number }[],
  ringFor: (h: never) => LngLat[],
  widthM = 150
): Feature<Polygon> {
  const rings: LngLat[][] = [];
  for (const h of holes) {
    const spine = ringFor(h as never);
    if (spine.length >= 2) {
      const cut = corridorPolygon(spine, widthM);
      // Reversed so each reads as a hole in the world ring, not an island.
      rings.push([...cut.geometry.coordinates[0]].reverse() as LngLat[]);
    }
  }
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [
        [[-180, -85], [180, -85], [180, 85], [-180, 85], [-180, -85]],
        ...rings,
      ] as number[][][],
    },
  };
}
